import React, { useState, useEffect } from 'react';
import Alert from "@amzn/awsui-components-core/alert";
import Badge from "@amzn/awsui-components-core/badge";
import Box from "@amzn/awsui-components-core/box";
import Button from "@amzn/awsui-components-core/button";
import Container from "@amzn/awsui-components-core/container";
import FormField from "@amzn/awsui-components-core/form-field";
import Icon from "@amzn/awsui-components-core/icon";
import Input from "@amzn/awsui-components-core/input";
import Modal from "@amzn/awsui-components-core/modal";
import SpaceBetween from "@amzn/awsui-components-core/space-between";
import Spinner from "@amzn/awsui-components-core/spinner";
import ActionButtons from 'src/components/shared/ActionButtons';
import RichTextEditor from 'src/components/shared/RichTextEditor';
import RichTextContent from 'src/components/shared/RichTextContent';
import { fetchShiftPassdown, createShiftPassdown, updateShiftPassdown, deleteShiftPassdown, ShiftPassdownEntry, CreateShiftPassdownInput } from 'src/services/passdowns-api';
import { useUser } from 'src/context/UserContext';
import { saveDraft, loadDraft, clearDraft } from 'src/utils/formDraft';
import FormattedDate from 'src/components/shared/FormattedDate';
import { useAlertMessages } from 'src/utils/useAlertMessages';
import { formatPlainTextToHtml } from 'src/utils/helpers';
import { MESSAGES } from 'src/utils/errorMessages';
import { passdownHistoryStyles } from 'src/components/shared/design-tokens';
import { requiredMark, requiredFieldHint } from 'src/components/shared/formHelpers';
import mockData from 'src/mock/mockData.json';

const DRAFT_KEY = 'passdown-oim';
const USE_MOCK_DATA = false;

/** Strips HTML tags to test whether rich-text content is actually present. */
const richTextToPlain = (html?: string) => (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

interface OIMFormData {
    category: string;
    oimNotableEvents: string;
    oimBolosPEs: string;
    oimInquiriesResolutions: string;
    oimIssuesBlockers: string;
    oimOtherNotes: string;
    oimNextOnDuty: string;
    // Tiptap ProseMirror JSON mirrors of the OIM rich-text fields (oimNextOnDuty
    // and the staffing numbers stay as-is).
    oimNotableEventsJson?: Record<string, any>;
    oimBolosPEsJson?: Record<string, any>;
    oimInquiriesResolutionsJson?: Record<string, any>;
    oimIssuesBlockersJson?: Record<string, any>;
    oimOtherNotesJson?: Record<string, any>;
    csStaffPresent: number; csStaffRequired: number;
    csirStaffPresent: number; csirStaffRequired: number;
    priorityAlarmsStaffPresent: number; priorityAlarmsStaffRequired: number;
    nonPriorityAlarmsStaffPresent: number; nonPriorityAlarmsStaffRequired: number;
    lsocStaffPresent: number; lsocStaffRequired: number;
}

const defaultForm: OIMFormData = {
    category: 'OIM',
    oimNotableEvents: '',
    oimBolosPEs: '',
    oimInquiriesResolutions: '',
    oimIssuesBlockers: '',
    oimOtherNotes: '',
    oimNextOnDuty: '',
    csStaffPresent: 0, csStaffRequired: 0,
    csirStaffPresent: 0, csirStaffRequired: 0,
    priorityAlarmsStaffPresent: 0, priorityAlarmsStaffRequired: 0,
    nonPriorityAlarmsStaffPresent: 0, nonPriorityAlarmsStaffRequired: 0,
    lsocStaffPresent: 0, lsocStaffRequired: 0,
};

const OIMPassdown = ({ showForm, setShowForm }: { showForm: boolean; setShowForm: (v: boolean) => void }) => {
    const { canModify } = useUser();
    const [passdowns, setPassdowns] = useState<ShiftPassdownEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [activeExpanded, setActiveExpanded] = useState(true);
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [form, setForm] = useState<OIMFormData>({ ...defaultForm });
    const { error, successMsg, setError, setSuccessMsg, alertRef } = useAlertMessages();
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; createdBy: string } | null>(null);

    const updateField = (field: keyof OIMFormData, value: string | number | Record<string, any>) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        loadPassdowns();
        const draft = loadDraft(DRAFT_KEY);
        if (draft) {
            setForm({ ...defaultForm, ...draft });
            setEditingId(draft.editingId || null);
            setShowForm(true);
            clearDraft(DRAFT_KEY);
        }
    }, []);

    const loadPassdowns = async () => {
        setLoading(true);
        try {
            if (USE_MOCK_DATA) {
                setPassdowns((mockData.oimpassdowns?.items || []) as ShiftPassdownEntry[]);
            } else {
                const data = await fetchShiftPassdown('OIM');
                setPassdowns(data.items || []);
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (b: ShiftPassdownEntry) => {
        setEditingId(b.passdownId);
        setForm({
            category: 'OIM',
            oimNotableEvents: b.oimNotableEvents || '',
            oimBolosPEs: b.oimBolosPEs || '',
            oimInquiriesResolutions: b.oimInquiriesResolutions || '',
            oimIssuesBlockers: b.oimIssuesBlockers || '',
            oimOtherNotes: b.oimOtherNotes || '',
            oimNextOnDuty: b.oimNextOnDuty || '',
            oimNotableEventsJson: b.oimNotableEventsJson,
            oimBolosPEsJson: b.oimBolosPEsJson,
            oimInquiriesResolutionsJson: b.oimInquiriesResolutionsJson,
            oimIssuesBlockersJson: b.oimIssuesBlockersJson,
            oimOtherNotesJson: b.oimOtherNotesJson,
            csStaffPresent: b.csStaffPresent || 0, csStaffRequired: b.csStaffRequired || 0,
            csirStaffPresent: b.csirStaffPresent || 0, csirStaffRequired: b.csirStaffRequired || 0,
            priorityAlarmsStaffPresent: b.priorityAlarmsStaffPresent || 0, priorityAlarmsStaffRequired: b.priorityAlarmsStaffRequired || 0,
            nonPriorityAlarmsStaffPresent: b.nonPriorityAlarmsStaffPresent || 0, nonPriorityAlarmsStaffRequired: b.nonPriorityAlarmsStaffRequired || 0,
            lsocStaffPresent: b.lsocStaffPresent || 0, lsocStaffRequired: b.lsocStaffRequired || 0,
        });
        setShowForm(true);
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm({ ...defaultForm });
        clearDraft(DRAFT_KEY);
    };

    const handleDeleteClick = (b: ShiftPassdownEntry) => {
        setDeleteTarget({ id: b.passdownId, createdBy: b.createdBy });
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteShiftPassdown(deleteTarget.id);
            setSuccessMsg('OIM Passdown deleted');
            await loadPassdowns();
        } catch (err: any) {
            setError(MESSAGES.RECORD_DELETE_ERROR);
        }
        setDeleteTarget(null);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const input: CreateShiftPassdownInput = { ...form };
            const isUpdate = !!editingId;
            if (editingId) { await updateShiftPassdown(editingId, input); }
            else { await createShiftPassdown(input); }
            resetForm();
            setSuccessMsg(isUpdate ? 'OIM Passdown updated' : 'OIM Passdown created');
            await loadPassdowns();
        } catch (err: any) {
            const msg = err.message || '';
            if (msg.includes('Not authenticated') || msg.includes('403')) {
                saveDraft(DRAFT_KEY, { ...form, editingId });
            }
            setError(editingId ? MESSAGES.RECORD_UPDATE_ERROR : MESSAGES.RECORD_CREATE_ERROR);
        } finally { setSubmitting(false); }
    };

    // Used for Next OIM on Duty, a single-line name from an Input. Render as
    // escaped plain text — no dangerouslySetInnerHTML needed.
    const renderField = (label: string, value?: string | number) => {
        if (!value && value !== 0) return null;
        return (
            <div className="passdown-entry__field">
                <h3>{label}</h3>
                <p>{value}</p>
            </div>
        );
    };

    // Render an OIM rich-text field read-only. Prefers the Tiptap JSON when present
    // (rendered safely via RichTextContent); otherwise falls back to the legacy
    // plain-text string so older records still render as before.
    const renderRichField = (label: string, json?: Record<string, any>, value?: string, exactCase = false) => {
        // `value` is the plain-text mirror of the JSON (both are emitted together by
        // the editor and co-persisted), so it reliably reflects whether the field has
        // real content — including when the editor was cleared to an empty document
        // (which still emits a JSON doc). Hide empty fields, matching the original
        // renderField behavior.
        if (!richTextToPlain(value)) return null;
        return (
            <div className="passdown-entry__field">
                {/* exactCase disables the SCSS uppercase transform for labels whose
                    casing must be preserved verbatim (e.g. "BOLOs/PEs"). */}
                <h3 style={exactCase ? { textTransform: 'none' } : undefined}>{label}</h3>
                <div className="passdown-rich-text">
                    <RichTextContent json={json as any} html={formatPlainTextToHtml(value || '')} />
                </div>
            </div>
        );
    };

    const renderStaffRow = (label: string, present?: number, required?: number) => {
        const pre = present ?? 0;
        const req = required ?? 0;
        const pct = req > 0 ? Math.round((pre / req) * 100) : 0;
        return (
            <tr>
                <td>{label}</td>
                <td>{pre}</td>
                <td>{req}</td>
                <td className={pct >= 100 ? 'staffing-pct--good' : pct >= 75 ? 'staffing-pct--warn' : 'staffing-pct--low'}>
                    {req > 0 ? `${pct}%` : '-'}
                </td>
            </tr>
        );
    };

    const maxStaff = 50;
    const hasStaffingError = [form.csStaffPresent, form.csStaffRequired, form.csirStaffPresent, form.csirStaffRequired, form.priorityAlarmsStaffPresent, form.priorityAlarmsStaffRequired, form.nonPriorityAlarmsStaffPresent, form.nonPriorityAlarmsStaffRequired, form.lsocStaffPresent, form.lsocStaffRequired].some(val => Number(val) > 50);
    const hasContent = [
        form.oimNotableEvents, form.oimBolosPEs, form.oimInquiriesResolutions,
        form.oimIssuesBlockers, form.oimOtherNotes,
    ].every(val => !!richTextToPlain(val)) && !!form.oimNextOnDuty.trim();
    const b0 = passdowns[0];

    return (
        <>
            <div ref={alertRef} tabIndex={-1}>
                { error &&
                    <div className="alert">
                        <Alert
                            dismissible
                            type="error"
                            onDismiss={() => setError(null)}
                        >
                            <strong>Error: </strong>{error}
                        </Alert>
                    </div>
                }
                { successMsg &&
                    <div className="alert">
                        <Alert
                            dismissible
                            type="success"
                            onDismiss={() => setSuccessMsg(null)}
                        >
                            {successMsg}
                        </Alert>
                    </div>
                }
            </div>

            {showForm && (
                <div className="passdown-form">
                    <FormField label={<>Notable Events{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            autoFocus
                            value={form.oimNotableEventsJson ?? formatPlainTextToHtml(form.oimNotableEvents || '')}
                            placeholder="Notable events..."
                            onChangeText={(text) => updateField('oimNotableEvents', text)}
                            onChangeJson={(json) => updateField('oimNotableEventsJson', json)}
                        />
                    </FormField>

                    <FormField label={<>BOLOs/PEs{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.oimBolosPEsJson ?? formatPlainTextToHtml(form.oimBolosPEs || '')}
                            placeholder="BOLOs and PEs..."
                            onChangeText={(text) => updateField('oimBolosPEs', text)}
                            onChangeJson={(json) => updateField('oimBolosPEsJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Inquiries and Resolutions/Next Steps{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.oimInquiriesResolutionsJson ?? formatPlainTextToHtml(form.oimInquiriesResolutions || '')}
                            placeholder="Inquiries and resolutions/next steps..."
                            onChangeText={(text) => updateField('oimInquiriesResolutions', text)}
                            onChangeJson={(json) => updateField('oimInquiriesResolutionsJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Issues/Blockers{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.oimIssuesBlockersJson ?? formatPlainTextToHtml(form.oimIssuesBlockers || '')}
                            placeholder="Issues or blockers..."
                            onChangeText={(text) => updateField('oimIssuesBlockers', text)}
                            onChangeJson={(json) => updateField('oimIssuesBlockersJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Other Notes{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.oimOtherNotesJson ?? formatPlainTextToHtml(form.oimOtherNotes || '')}
                            placeholder="Other notes..."
                            onChangeText={(text) => updateField('oimOtherNotes', text)}
                            onChangeJson={(json) => updateField('oimOtherNotesJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Next OIM on Duty{requiredMark()}</>} stretch>
                        <Input
                            placeholder="Next OIM on duty..."
                            value={form.oimNextOnDuty ?? ''}
                            onChange={({ detail }) => updateField('oimNextOnDuty', detail.value)}
                        />
                    </FormField>

                    <FormField label="STAFFING" stretch></FormField>
                    <div className="passdown-form__staffing-grid">
                        <div className="passdown-form__staffing-row passdown-form__staffing-row--header">
                            <span className="passdown-form__staffing-col-header">TEAM</span>
                            <span className="passdown-form__staffing-col-header">Present</span>
                            <span className="passdown-form__staffing-col-header">Required</span>
                        </div>
                        {[
                            { label: 'CS', pKey: 'csStaffPresent' as const, rKey: 'csStaffRequired' as const },
                            { label: 'CSIR', pKey: 'csirStaffPresent' as const, rKey: 'csirStaffRequired' as const },
                            { label: 'Priority Alarms', pKey: 'priorityAlarmsStaffPresent' as const, rKey: 'priorityAlarmsStaffRequired' as const },
                            { label: 'Non-Priority Alarms', pKey: 'nonPriorityAlarmsStaffPresent' as const, rKey: 'nonPriorityAlarmsStaffRequired' as const },
                            { label: 'LSOC', pKey: 'lsocStaffPresent' as const, rKey: 'lsocStaffRequired' as const },
                        ].map(({ label, pKey, rKey }) => (
                            <div key={label} className="passdown-form__staffing-row">
                                <span className="passdown-form__staffing-label">{label}</span>
                                <Input
                                    inputMode="numeric"
                                    type="number"
                                    invalid={Number(form[pKey]) > maxStaff}
                                    value={String(form[pKey] || 0)}
                                    onChange={({ detail }) => updateField(pKey, Math.max(0, parseInt(detail.value) || 0))}
                                />
                                <Input
                                    inputMode="numeric"
                                    type="number"
                                    invalid={Number(form[rKey]) > maxStaff}
                                    value={String(form[rKey] || 0)}
                                    onChange={({ detail }) => updateField(rKey, Math.max(0, parseInt(detail.value) || 0))}
                                />
                            </div>
                        ))}
                    </div>

                    <div className="passdown-form__actions">
                        {requiredFieldHint()}
                        <div className="passdown-form__actions-buttons">
                            <Button variant="link" onClick={resetForm}>Cancel</Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={submitting || hasStaffingError || !hasContent}
                            >
                                {submitting ? 'Submitting...' : (editingId ? 'Update OIM Passdown' : 'Submit OIM Passdown')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {loading && <Spinner />}
            {!loading && passdowns.length === 0 && !showForm && <p className="section-card__placeholder">No OIM passdowns yet.</p>}

            {!loading && b0 && (
                <>
                    <div className="passdown-list">
                        <div className="passdown-entry">
                            <div className="passdown-entry__icon">
                                <span className="passdown-entry__meta">
                                    <FormattedDate date={b0.createdAt} /> &middot; by {b0.createdBy}
                                    <span className="badge-custom">
                                        <Badge color="green">Active</Badge>
                                    </span>
                                    { b0.updatedAt && b0.updatedAt !== b0.createdAt && (
                                        <span className="passdown-entry__last-updated-info">Last updated on <FormattedDate date={b0.updatedAt} /> &middot; by {b0.updatedBy}</span>
                                    )}
                                </span>
                                <div className="passdown-entry__actions">
                                    {canModify('oimShiftPassdown', b0.createdBy) && (
                                        <ActionButtons
                                            onEdit={() => handleEdit(b0)}
                                            onDelete={() => handleDeleteClick(b0)}
                                            disabled={showForm}
                                        />
                                    )}
                                    <span className="passdown-history__toggle" onClick={() => setActiveExpanded(prev => !prev)}>
                                        <Icon
                                            name={activeExpanded ? "caret-up-filled" : "caret-down-filled"}
                                            variant="subtle"
                                        />
                                    </span>
                                </div>
                            </div>

                            {!activeExpanded && <p style={{marginTop: "8px"}}>{richTextToPlain(b0.oimNotableEvents) || richTextToPlain(b0.oimInquiriesResolutions)} ...</p>}

                            {activeExpanded && (
                                <>
                                    <div className="passdown-entry__fields-grid">
                                        {renderRichField('Notable Events', b0.oimNotableEventsJson, b0.oimNotableEvents)}
                                        {renderRichField('BOLOs/PEs', b0.oimBolosPEsJson, b0.oimBolosPEs, true)}
                                        {renderRichField('Inquiries and Resolutions/Next Steps', b0.oimInquiriesResolutionsJson, b0.oimInquiriesResolutions)}
                                        {renderRichField('Issues/Blockers', b0.oimIssuesBlockersJson, b0.oimIssuesBlockers)}
                                        {renderRichField('Other Notes', b0.oimOtherNotesJson, b0.oimOtherNotes)}
                                        {renderField('Next OIM on Duty', b0.oimNextOnDuty)}
                                    </div>
                                    <div className="passdown-entry__field">
                                        <div className="passdown-staffing-header">
                                            <h4>STAFFING</h4>
                                        </div>
                                        <table className="passdown-staffing-table">
                                            <thead><tr><th>Team</th><th>Present</th><th>Required</th><th>%</th></tr></thead>
                                            <tbody>
                                                {renderStaffRow('CS', b0.csStaffPresent, b0.csStaffRequired)}
                                                {renderStaffRow('CSIR', b0.csirStaffPresent, b0.csirStaffRequired)}
                                                {renderStaffRow('Priority Alarms', b0.priorityAlarmsStaffPresent, b0.priorityAlarmsStaffRequired)}
                                                {renderStaffRow('Non-Priority Alarms', b0.nonPriorityAlarmsStaffPresent, b0.nonPriorityAlarmsStaffRequired)}
                                                {renderStaffRow('LSOC', b0.lsocStaffPresent, b0.lsocStaffRequired)}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {passdowns.length > 1 && (
                        <div className="passdown-history">
                            <h4 className="passdown-history__title">📋 OIM PASSDOWN HISTORY</h4>
                            <div className="passdown-history__list">
                                {(showAllHistory ? passdowns.slice(1) : passdowns.slice(1, 6)).map((b, idx) => {
                                    const isExpanded = expandedId === b.passdownId;
                                    return (
                                        <span key={b.passdownId} style={{marginBottom: "0.8rem"}}>
                                            <Container style={passdownHistoryStyles}>
                                                <span className="passdown-history__dot" />
                                                <div className="passdown-history__content">
                                                    <div className="passdown-history__top">
                                                        <span className="passdown-history__date">
                                                            <FormattedDate date={b.createdAt} /> &middot; by {b.createdBy}
                                                            { b.updatedAt && b.updatedAt !== b.createdAt && (
                                                                <span className="passdown-entry__last-updated-info">Last updated on <FormattedDate date={b.updatedAt} /> &middot; by {b.updatedBy}</span>
                                                            )}
                                                        </span>
                                                        <div className="passdown-history__top-right">
                                                            {canModify('oimShiftPassdown', b.createdBy) && (
                                                                <ActionButtons
                                                                    onEdit={() => handleEdit(b)}
                                                                    onDelete={() => handleDeleteClick(b)}
                                                                    disabled={showForm}
                                                                />
                                                            )}
                                                            <span className="passdown-history__toggle" onClick={() => setExpandedId(isExpanded ? null : b.passdownId)}>
                                                                <Icon
                                                                    name={isExpanded ? "caret-up-filled" : "caret-down-filled"}
                                                                    variant="subtle"
                                                                />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {isExpanded && (
                                                        <div className="passdown-history__details">
                                                            <div className="passdown-entry__fields-grid">
                                                                {renderRichField('Notable Events', b.oimNotableEventsJson, b.oimNotableEvents)}
                                                                {renderRichField('BOLOs/PEs', b.oimBolosPEsJson, b.oimBolosPEs, true)}
                                                                {renderRichField('Inquiries and Resolutions/Next Steps', b.oimInquiriesResolutionsJson, b.oimInquiriesResolutions)}
                                                                {renderRichField('Issues/Blockers', b.oimIssuesBlockersJson, b.oimIssuesBlockers)}
                                                                {renderRichField('Other Notes', b.oimOtherNotesJson, b.oimOtherNotes)}
                                                                {renderField('Next OIM on Duty', b.oimNextOnDuty)}
                                                            </div>
                                                            <div className="passdown-entry__field">
                                                                <div className="passdown-staffing-header">
                                                                    <h4>STAFFING</h4>
                                                                </div>
                                                                <table className="passdown-staffing-table">
                                                                    <thead><tr><th>Team</th><th>Present</th><th>Required</th><th>%</th></tr></thead>
                                                                    <tbody>
                                                                        {renderStaffRow('CS', b.csStaffPresent, b.csStaffRequired)}
                                                                        {renderStaffRow('CSIR', b.csirStaffPresent, b.csirStaffRequired)}
                                                                        {renderStaffRow('Priority Alarms', b.priorityAlarmsStaffPresent, b.priorityAlarmsStaffRequired)}
                                                                        {renderStaffRow('Non-Priority Alarms', b.nonPriorityAlarmsStaffPresent, b.nonPriorityAlarmsStaffRequired)}
                                                                        {renderStaffRow('LSOC', b.lsocStaffPresent, b.lsocStaffRequired)}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Container>
                                        </span>
                                    );
                                })}
                            </div>
                            {passdowns.length > 6 && (
                                <div data-size='sm'>
                                    <Button
                                        variant="inline-link"
                                        ariaLabel={showAllHistory ? 'view less' : 'view more'}
                                        onClick={() => setShowAllHistory(prev => {
                                            if (prev) {
                                                setTimeout(() => alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
                                            }
                                            return !prev;
                                        })}
                                    >
                                        {showAllHistory ? 'View less' : 'View more'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {deleteTarget && (
                <Modal
                    visible
                    header="Delete OIM Passdown"
                    onDismiss={() => setDeleteTarget(null)}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                                <Button variant="primary" onClick={confirmDelete}>Delete</Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    Are you sure you want to delete this OIM passdown by <strong>{deleteTarget.createdBy}</strong>? This cannot be undone.
                </Modal>
            )}
        </>
    );
};

export default OIMPassdown;
